/** Git LFS pointer file (text committed in place of large blobs). */
export interface LfsPointer {
  oid: string;
  size: number;
}

const POINTER_PREFIX = 'version https://git-lfs.github.com/spec/v1';

export function buildLfsPointer(oid: string, size: number): string {
  const normalized = oid.startsWith('sha256:') ? oid : `sha256:${oid}`;
  return `${POINTER_PREFIX}\noid ${normalized}\nsize ${size}\n`;
}

export function parseLfsPointer(text: string): LfsPointer | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith(POINTER_PREFIX)) return null;
  let oid: string | null = null;
  let size: number | null = null;
  for (const line of trimmed.split('\n')) {
    const o = /^oid\s+(\S+)/i.exec(line.trim());
    if (o) oid = o[1]!;
    const s = /^size\s+(\d+)/i.exec(line.trim());
    if (s) size = parseInt(s[1]!, 10);
  }
  if (!oid || size == null || Number.isNaN(size)) return null;
  return { oid, size };
}

export function parseLfsPointerBytes(bytes: Uint8Array): LfsPointer | null {
  if (bytes.length > 512) return null;
  try {
    return parseLfsPointer(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

/** Item photo / audio / video under database/…/_images|_audio|_video/. */
export function isItemMediaRepoPath(repoPath: string): boolean {
  return /\/_images\//i.test(repoPath) || /\/_audio\//i.test(repoPath) || /\/_video\//i.test(repoPath);
}

/** True when read logic should treat the path as media (pointer or blob). */
export function pathUsesGitLfs(repoPath: string): boolean {
  return isItemMediaRepoPath(repoPath);
}

/** Only large uploads use Git LFS; smaller files are stored as normal blobs (GitHub download works). */
export const ITEM_MEDIA_LFS_MIN_BYTES = 10 * 1024 * 1024;

export function shouldWriteMediaWithGitLfs(repoPath: string, byteLength: number): boolean {
  return isItemMediaRepoPath(repoPath) && byteLength >= ITEM_MEDIA_LFS_MIN_BYTES;
}
